
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import { Box, Button, Typography } from '@mui/material';
import DynamicColumns from './components/DynamicColumns';
import DetailPanel from './components/DetailPanel';
import RowTypeSelector from './components/RowTypeSelector';
import { groupItemsByTable, calculateSubtotal } from './utils/tableUtils';
import { toNumber } from './utils/toNumber';
import { updateMpiWorkItem } from '../../../../../actions/apiMpiWorkItems';
import _ from 'lodash';

const MpiTable = ({
  items,
  vehicleId,
  defaultLaborRate,
  onDelete,
  onRefresh,
  rowTotals,
  setRowTotals,
  dynamicHeaders,
  sectionRule,
  tableRef,
  enableRowSelection,
  form,
  section,
  work_item_presets = [],
  users = [],
  current_user_profile_id,
  current_dealership,
  isDisabled,
  onDeleteAttachment,
  confirmingTask,
  handleBulkActionConfirm,
  handleBulkActionButtonClick,
  dispatch,
  sections,
  setError,
  isDataReady,
  tab,
  addCustomRow,
}) => {
  const [tableKey, setTableKey] = useState(0);
  const [inputValues, setInputValues] = useState({});
  const [rowSelection, setRowSelection] = useState({});

  const itemsKey = useMemo(() => JSON.stringify(items.map(item => item.id)), [items]);

  // Reset state on tab change
  useEffect(() => {
    setTableKey(prev => prev + 1);
    setInputValues({});
    setRowSelection({});
  }, [tab]);

  useEffect(() => {
    setTableKey(prev => prev + 1);
  }, [itemsKey]);

  const tables = useMemo(() => {
    return groupItemsByTable(items, sectionRule, defaultLaborRate).map(table => ({
      ...table,
      items: table.items.filter(item => item && typeof item === 'object' && 'layout_type' in item),
    }));
  }, [items, sectionRule, defaultLaborRate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getUserStamp = (item, field) => {
    const uid = item[`${field}_by_uid`];
    const date = item[`${field}_by_date`];

    if (!uid || !date) return null;

    const userList = Array.isArray(users) ? users : [];
    const user = _.find(userList, u => u.id === uid);
    const userName = user?.user_name || user?.email || 'iPacketAdmin';

    return (
      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#555', fontWeight: 500 }}>
        {userName} <br /> {formatDate(date)}
      </Typography>
    );
  };

  const canEditField = (item, field, headerAllowedProfiles) => {
    const presets = Array.isArray(work_item_presets) ? work_item_presets : [];
    const preset = _.find(presets, p => p.id === item.work_item_preset_id) || {};

    const profileId = String(current_user_profile_id || 'default_user').toLowerCase();
    if (['admin', 'super_admin'].includes(profileId)) return true;

    const presetAllowedProfiles = preset[`${field}_allowed_profiles`];
    const profilesToCheck = presetAllowedProfiles
      ? presetAllowedProfiles.split(',').map(Number).filter(Boolean)
      : headerAllowedProfiles
      ? headerAllowedProfiles.split(',').map(Number).filter(Boolean)
      : [];

    const editableField = `${field}_editable`;
    const lockedField = `${field}_locked`;
    const isEditable = preset[editableField] !== false;
    const isLocked = preset[lockedField] === true;

    const hasPermission = profilesToCheck.length === 0 || profilesToCheck.includes(Number(current_user_profile_id));

    return isEditable && !isLocked && hasPermission;
  };

  const updateRowTotals = (updatedItem, field) => {
    if (!updatedItem.show_mpi || !updatedItem.active || updatedItem.deleted) {
      return {
        estimate_total: toNumber(updatedItem.estimate_total, 0),
        parts_total: toNumber(updatedItem.parts_total, 0),
        labor_total: toNumber(updatedItem.labor_total, 0),
      };
    }

    let calculatedTotal = toNumber(updatedItem.estimate_total, 0, 2);
    let partsTotal = toNumber(updatedItem.parts_total, 0, 2);
    let laborTotal = toNumber(updatedItem.labor_total, 0, 2);
    const isManuallyEdited = updatedItem.isManuallyEdited || false;

    const effectiveLaborRate = toNumber(updatedItem.labor_rate, 0, 2) === 0.0
      ? toNumber(defaultLaborRate, 100, 2)
      : toNumber(updatedItem.labor_rate, 0, 2);

    if (!isManuallyEdited) {
      if (['parts', 'quantity'].includes(field)) {
        const parts = toNumber(updatedItem.parts, 0, 2);
        const quantity = toNumber(updatedItem.quantity, 0, 2);
        partsTotal = parts * quantity;
      }
      if (['labor_hours', 'labor_rate'].includes(field)) {
        const laborHours = toNumber(updatedItem.labor_hours, 0, 2);
        laborTotal = laborHours * effectiveLaborRate;
      }
      if (['parts_total', 'labor_total'].includes(field)) {
        partsTotal = toNumber(updatedItem.parts_total, 0, 2);
        laborTotal = toNumber(updatedItem.labor_total, 0, 2);
        calculatedTotal = partsTotal + laborTotal;
      }
    } else if (isManuallyEdited && ['estimate_total', 'parts_total', 'labor_total'].includes(field)) {
      calculatedTotal = toNumber(updatedItem.estimate_total, 0, 2);
      partsTotal = toNumber(updatedItem.parts_total, 0, 2);
      laborTotal = toNumber(updatedItem.labor_total, 0, 2);
    }

    setRowTotals(prev => ({
      ...prev,
      [updatedItem.name]: {
        laborTotal: laborTotal.toFixed(2),
        partsTotal: partsTotal.toFixed(2),
        total: calculatedTotal.toFixed(2),
        laborRate: effectiveLaborRate.toFixed(2),
        forceUpdate: Date.now(),
      },
    }));

    return { estimate_total: calculatedTotal, parts_total: partsTotal, labor_total: laborTotal };
  };

  const handleCellEdit = async (row, column, value) => {
    if (!row?.original || !column?.id) return;

    const field = column.id;
    const header = dynamicHeaders.find(h => h.field === field);
    const inputType = header?.inputtype || (row.original[`${field}_inputtype`] || 'text');
    const isCustomHeader = field.startsWith('custom_header_');
    let processedValue = value;
    let isManuallyEdited = row.original.isManuallyEdited || false;

    if (['labor_hours', 'labor_rate', 'quantity', 'parts', 'estimate_total', 'parts_total', 'labor_total'].includes(field)) {
      processedValue = toNumber(value, 0, 2);
      if (field === 'labor_rate' && processedValue === 0.0) {
        processedValue = toNumber(defaultLaborRate, 0, 2);
      }
      if (['estimate_total', 'parts_total', 'labor_total'].includes(field)) {
        isManuallyEdited = true;
      }
    } else if (booleanFields.includes(field)) {
      processedValue = value === 'true' || value === true;
    } else if (isCustomHeader && inputType === 'userstamp') {
      processedValue = value ? 'true' : 'false';
    } else {
      processedValue = value;
    }

    const data = { [field]: processedValue, isManuallyEdited };
    if (booleanFields.includes(field) || field === 'name' || inputType === 'userstamp' || inputType === 'textstamp') {
      data[`${field}_by_uid`] = current_user_profile_id || 'default_user';
      data[`${field}_by_date`] = new Date().toISOString();
    }
    data.vehicle_id = vehicleId;
    data.form = row.original.form || form;
    data.section = row.original.section || section;

    const updatedItem = { ...row.original, [field]: processedValue, isManuallyEdited };
    const { estimate_total, parts_total, labor_total } = updateRowTotals(updatedItem, field);

    if (['parts', 'quantity'].includes(field)) {
      data.parts_total = parts_total;
      data.estimate_total = parts_total + toNumber(updatedItem.labor_total, 0, 2);
    }
    if (['labor_hours', 'labor_rate'].includes(field)) {
      data.labor_total = labor_total;
      data.estimate_total = toNumber(updatedItem.parts_total, 0, 2) + labor_total;
    }
    if (['parts_total', 'labor_total'].includes(field)) {
      data.estimate_total = parts_total + labor_total;
    } else if (['estimate_total', 'parts_total', 'labor_total'].includes(field) && processedValue !== '') {
      data.estimate_total = processedValue;
      data.parts_total = field === 'parts_total' ? processedValue : parts_total;
      data.labor_total = field === 'labor_total' ? processedValue : labor_total;
    }

    try {
      await dispatch(updateMpiWorkItem(row.original.id, data));
      onRefresh(Date.now());
      setTableKey(prev => prev + 1);
    } catch (error) {
      if (typeof setError === 'function') {
        setError(`Failed to update work item: ${error.message}`);
      }
    }
  };

  const handleInputChange = (rowId, field, value) => {
    setInputValues(prev => ({
      ...prev,
      [`${rowId}-${field}`]: value,
    }));
  };

  const handleInputCommit = (row, column, value) => {
    handleCellEdit(row, column, value);
    setInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[`${row.original.id}-${column.id}`];
      return newValues;
    });
  };

  const dynamicColumns = DynamicColumns({
    dynamicHeaders,
    handleCellEdit,
    handleInputChange,
    handleInputCommit,
    canEditField,
    getUserStamp,
    work_item_presets,
    setError,
    inputValues,
  });

  if (!isDataReady) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {tables.map((table, idx) => (
        <div key={`${section}-${idx}-${itemsKey}`} style={{ marginBottom: '2rem' }}>
          {table.title && <h6 className="mb-2">{table.title}</h6>}
          <MaterialReactTable
            key={`${section}-${idx}-${itemsKey}`}
            tableInstanceRef={tableRef}
            columns={[
              ...dynamicColumns,
              {
                accessorKey: 'action',
                header: '',
                size: 50,
                enableEditing: false,
                Cell: ({ row }) => (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => onDelete(row.original.name, row.original.id)}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
            data={table.items.map(item => ({ ...item, id: item.id }))}
            enableEditing
            editDisplayMode="cell"
            enableRowSelection={enableRowSelection ? (row) => {
              return (
                (row.original.ro_status === 'pending' ||
                 row.original.ro_status === 'getapproved' ||
                 row.original.ro_status === '' ||
                 row.original.ro_status === null) &&
                !isDisabled
              );
            } : false}
            state={{ rowSelection }}
            onRowSelectionChange={setRowSelection}
            getRowId={(row) => row.id.toString()}
            initialState={{
              density: 'compact',
              columnVisibility: { labor_rate: false },
              pagination: { pageSize: 1000 },
            }}
            muiTableBodyCellProps={{ sx: { padding: '0.5rem' } }}
            renderDetailPanel={({ row }) => (
              <DetailPanel
                row={row}
                active_vehicle={useSelector(state => state.settings_refresh?.active_vehicle || {})}
                current_dealership={current_dealership}
                onDeleteAttachment={onDeleteAttachment}
                tab={tab}
              />
            )}
          />
          {confirmingTask ? (
            <Box sx={{ display: 'flex', gap: '1rem', mt: 1 }}>
              <Button
                variant="contained"
                color={confirmingTask === 'Approve' ? 'success' : 'error'}
                size="small"
                onClick={() => {
                  const selectedRowIds = Object.keys(rowSelection).map(id => parseInt(id, 10));
                  handleBulkActionButtonClick(confirmingTask, section, selectedRowIds);
                  setRowSelection({});
                }}
                disabled={Object.keys(rowSelection).length === 0 || isDisabled}
              >
                Confirm {confirmingTask} Selected
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => {
                  handleBulkActionConfirm(null);
                  setRowSelection({});
                }}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: '1rem', mt: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleBulkActionConfirm('Approve')}
                disabled={Object.keys(rowSelection).length === 0 || isDisabled}
              >
                Approve Selected
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleBulkActionConfirm('Deny')}
                disabled={Object.keys(rowSelection).length === 0 || isDisabled}
              >
                Deny Selected
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  addCustomRow(section);
                }}
                disabled={isDisabled}
              >
                Add Custom Row
              </Button>
            </Box>
          )}
          {table.subtotal > 0 && (
            <div style={{ textAlign: 'right', marginTop: '0.5rem', fontWeight: 'bold' }}>
              Subtotal: ${table.subtotal.toFixed(2)}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

const booleanFields = [
  'show_mpi', 'active', 'deleted', 'auto_assign', 'pass', 'recommend', 'required',
  'approved', 'declined', 'completed', 'dvi_pdf_show', 'lock_pass', 'lock_recommend',
  'lock_required', 'lock_labor_rate', 'lock_labor_hours', 'lock_labor_total',
  'lock_parts', 'lock_parts_total', 'lock_quantity', 'lock_estimate_total',
  'lock_approved', 'lock_declined', 'lock_completed', 'lock_estimate', 'lock_counter',
];

export default MpiTable;